import React from 'react';
import {
  Grid,
  GridColumn as Column,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridCustomCellProps
} from '@progress/kendo-react-grid';
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon } from '@progress/kendo-svg-icons';
import moment from 'moment';
import SelectedAssetMetricsReadDto from '../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import { faDiagramProject } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { enMessages } from '../../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { formatSmartDate } from '../../../../../Shared/Utilities/DateFormattingUtils';

interface Props {
  assetData: SelectedAssetMetricsReadDto;
  selectedGroupId: number | null;
  onGroupSelect: (groupId: number | null) => void;
}

const ComplianceCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();
  
  // Get the localized compliance status text
  let complianceText = '';
  if (props.dataItem.complianceStatus === 'Compliant') {
    complianceText = localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant);
  } else if (props.dataItem.complianceStatus === 'NonCompliant') {
    complianceText = localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant);
  } else if (props.dataItem.complianceStatus === 'PartiallyCompliant') {
    complianceText = localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant);
  } else {
    complianceText = localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown);
  }

  return (
    <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
      >
        <div className="flex flex-wrap" style={{ cursor: 'pointer' }} title={complianceText}>
          <div className="flex items-center justify-center h-full w-full">
            <SvgIcon icon={circleIcon} size="xlarge" color={props.dataItem.complianceStatus === 'Compliant' ? '#3FA43F' : props.dataItem.complianceStatus === 'NonCompliant' ? '#FF3630' : props.dataItem.complianceStatus === 'PartiallyCompliant' ? '#FFBF00' : '#85868E'} />
          </div>
        </div>
      </Tooltip>
    </td>
  );
};

const ConnectivityCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();
  
  // Get the localized connectivity status text
  let connectivityText = '';
  if (props.dataItem.connectivityStatus === 'Online') {
    connectivityText = localizationService.toLanguageString('custom.online', enMessages.custom.online);
  } else if (props.dataItem.connectivityStatus === 'Offline') {
    connectivityText = localizationService.toLanguageString('custom.oneOrMoreDevicesAreOffline', enMessages.custom.oneOrMoreDevicesAreOffline);
  } else if (props.dataItem.connectivityStatus === 'Partial') {
    connectivityText = localizationService.toLanguageString('custom.partialConnected', enMessages.custom.partialConnected);
  } else {
    connectivityText = localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown);
  }

  return (
    <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
      >
        <div className="flex flex-wrap" style={{ cursor: 'pointer' }} title={connectivityText}>
          <div className="flex items-center justify-center h-full w-full">
            <SvgIcon icon={circleIcon} size="xlarge" color={props.dataItem.connectivityStatus === 'Online' ? '#3FA43F' : props.dataItem.connectivityStatus === 'Offline' ? '#FF3630' : props.dataItem.connectivityStatus === 'Partial' ? '#FFBF00' : '#85868E'} />
          </div>
        </div>
      </Tooltip>
    </td>
  );
};

const LastActivityCell = (props: any) => {
  const localizationService = useLocalization();
  const { displayText: lastSeenText, tooltipText } = formatSmartDate(
    props.dataItem.lastActivity,
    localizationService,
    enMessages
  );

  return (
    <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastSeenText}
        </span>
      </Tooltip>
    </td>
  );
};

const ConnectivityHeaderCell = (props) => {
  const localizationService = useLocalization();
  
  const TooltipContent = () => (
    <div className="p-2">
      <div>{localizationService.toLanguageString('custom.connectivityTooltip', enMessages.custom.connectivityTooltip)}</div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#3FA43F" />
        <span>{localizationService.toLanguageString("custom.online", enMessages.custom.online)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#FF3630" />
        <span>{localizationService.toLanguageString("custom.offline", enMessages.custom.offline)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#FFBF00" />
        <span>{localizationService.toLanguageString("custom.partialConnected", enMessages.custom.partialConnected)}</span>
      </div>
    </div>
  );

  return (
    <Tooltip 
      anchorElement="target" 
      position="bottom"
      parentTitle={true}
      content={TooltipContent}
    >
      <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }} title=" ">
        {props.title}
      </span>
    </Tooltip>
  );
};

const ComplianceHeaderCell = (props) => {
  const localizationService = useLocalization();
  
  const TooltipContent = () => (
    <div className="p-2">
      <div>{localizationService.toLanguageString('custom.complianceTooltip', enMessages.custom.complianceTooltip)}</div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#FF3630" />
        <span>{localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#FFBF00" />
        <span>{localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#3FA43F" />
        <span>{localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#85868E" />
        <span>{localizationService.toLanguageString("custom.unknown", enMessages.custom.unknown)}</span>
      </div>
    </div>
  );

  return (
    <Tooltip 
      anchorElement="target" 
      position="bottom"
      parentTitle={true}
      content={TooltipContent}
    >
      <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }} title=" ">
        {props.title}
      </span>
    </Tooltip>
  );
};

const TypeCell = (props: GridCustomCellProps) => {
  return (
    <td>
      <div className="flex items-center justify-center h-full w-full" title={props.dataItem.entityType}>
        <FontAwesomeIcon icon={faDiagramProject} style={{width: '20px', height: '20px'}}/>
      </div>
    </td>
  );
};



const EmptyHeaderCell = () => <th></th>;

const GroupsTableComponent: React.FC<Props> = ({ assetData, selectedGroupId, onGroupSelect }) => {
  const localizationService = useLocalization();

  const groups = React.useMemo(
    () => {
      const groupsData = assetData.assetConfigurationDetails!.groups;
      
      // Custom sorting function: connectivity first, then compliance, with logical priority order
      return [...groupsData].sort((a, b) => {
        // First sort by connectivity status (Offline first - most critical, then Partial, Online, Unknown)
        const connectivityOrder = { 'Offline': 0, 'Partial': 1, 'Online': 2, 'Unknown': 3 };
        const aConnectivityOrder = connectivityOrder[a.connectivityStatus] ?? 3;
        const bConnectivityOrder = connectivityOrder[b.connectivityStatus] ?? 3;
        
        if (aConnectivityOrder !== bConnectivityOrder) {
          return aConnectivityOrder - bConnectivityOrder;
        }
        
        // If connectivity is the same, sort by compliance status (NonCompliant first - most critical, then PartiallyCompliant, Compliant, Unknown)
        const complianceOrder = { 'Unknown': 0, 'NonCompliant': 1, 'PartiallyCompliant': 2, 'Compliant': 3 };
        const aComplianceOrder = complianceOrder[a.complianceStatus] ?? 3;
        const bComplianceOrder = complianceOrder[b.complianceStatus] ?? 3;
        
        return aComplianceOrder - bComplianceOrder;
      });
    },
    [assetData]
  );


  const DevicesCell = (props: GridCustomCellProps) => {
    const devicesCount = assetData.assetConfigurationDetails.endpoints.filter(x => x.groupId === props.dataItem.id).length
  
    return (
      <td>
        <div>
          {devicesCount}
        </div>  
      </td>
    );
  };
  
  // Controlled selection object
  const select = selectedGroupId ? { [selectedGroupId.toString()]: true } : {};

  const onSelectionChange = React.useCallback((event: GridSelectionChangeEvent) => {
    const selectedKey = Object.keys(event.select)[0];
    if (selectedKey) {
      onGroupSelect?.(parseInt(selectedKey, 10));
    } else {
      onGroupSelect?.(null);
    }
    // No setSelect here!
  }, [onGroupSelect]);

  const onHeaderSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
    if (event.select[0] && groups.length > 0) {
      onGroupSelect?.(Number(groups[0].id));
    } else {
      onGroupSelect?.(null);
    }
    // No setSelect here!
  }, [groups, onGroupSelect]);
 
  return (
    <div className="flex flex-col h-full min-h-0 checkbox-outline-soft-override">
      <div className="text-lg font-bold mb-4">Groups</div>
      <Grid
        data={groups}
        style={{ overflow: 'auto' }}
        dataItemKey="id"
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'single'
        }}
        scrollable={'scrollable'}
        select={select}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
      >
        <Column
          field="selected"
          width="40px"
          columnType="checkbox"
          cells={{ headerCell: EmptyHeaderCell }}
        />
        <Column field="entityType" title=" " cells={{ data: TypeCell }} width="45px" />
        <Column field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} />
        <Column field="compliance" title={localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance)} cells={{ data: ComplianceCell }} headerCell={ComplianceHeaderCell}/>
        <Column field="connectivity" title={localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)} cells={{ data: ConnectivityCell }} headerCell={ConnectivityHeaderCell} />
        <Column field="devices" title={localizationService.toLanguageString('custom.points', enMessages.custom.points)} cells={{ data: DevicesCell }} />
        <Column field="lastActivity" title={localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity)} cell={LastActivityCell} />
      </Grid>
    </div>
  );
};

export default GroupsTableComponent; 